import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { NavLink, useNavigate } from 'react-router-dom';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  LabelList,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const pad2 = (n) => String(n).padStart(2, '0');
const toDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const safeArray = (val) => (Array.isArray(val) ? val : []);

const parseDollarsToCents = (val) => {
  try {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number' && Number.isFinite(val)) return Math.max(0, Math.round(val * 100));
    const s = String(val).trim();
    if (!s) return 0;
    const cleaned = s.startsWith('$') ? s.slice(1) : s;
    const n = Number(cleaned);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.round(n * 100));
  } catch {
    return 0;
  }
};

const formatMoneyFromCents = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return `$${(n / 100).toFixed(2)}`;
};

const escapeCsv = (val) => {
  const s = String(val ?? '');
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = ({ filename, rows }) => {
  const header = rows.length ? Object.keys(rows[0]) : [];
  const lines = [header.map(escapeCsv).join(',')];
  for (const row of rows) {
    lines.push(header.map((k) => escapeCsv(row?.[k])).join(','));
  }
  const csv = `${lines.join('\n')}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const easternFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hour12: false,
});

const bucketKey = (epochSeconds, granularity) => {
  try {
    const dt = new Date(Number(epochSeconds) * 1000);
    if (Number.isNaN(dt.getTime())) return '';
    const parts = easternFormatter.formatToParts(dt);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    const hour = parts.find((p) => p.type === 'hour')?.value || '';
    if (!year || !month || !day) return '';
    const dateKey = `${year}-${month}-${day}`;
    if (granularity === 'hour') {
      if (!hour) return '';
      return `${dateKey} ${hour}:00`;
    }
    return dateKey;
  } catch {
    return '';
  }
};

const sumCents = (orders) => {
  return safeArray(orders).reduce((acc, o) => {
    const cents = o?.amount_total_cents ?? o?.amount_total;
    const n = Number(cents);
    return acc + (Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);
  }, 0);
};

const normalizeChannel = (order) => {
  const channel = String(order?.salesChannel || '').trim().toUpperCase();
  if (channel) return channel;
  return order?.isPos ? 'POS' : 'ONLINE';
};

const normalizePayType = (order) => {
  const isPos = Boolean(order?.isPos);
  const mode = String(order?.posPaymentMode || '').trim().toUpperCase();
  return isPos && mode === 'PAY_AT_POS' ? 'pay_at_store' : 'pay_online';
};

export default function AdminOnlineSalesReport() {
  const navigate = useNavigate();
  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [rangeMode, setRangeMode] = useState('lookback');
  const [lookbackUnit, setLookbackUnit] = useState('days');
  const [lookbackValue, setLookbackValue] = useState('7');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [orderStatusFilter, setOrderStatusFilter] = useState('ALL');
  const [channelFilter, setChannelFilter] = useState('ALL');
  const [payTypeFilter, setPayTypeFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [mealFilter, setMealFilter] = useState('ALL');
  const [locationFilter, setLocationFilter] = useState('ALL');
  const [locations, setLocations] = useState([]);
  const [granularity, setGranularity] = useState('auto');

  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [searching, setSearching] = useState(false);

  const [topMode, setTopMode] = useState('revenue');

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  useEffect(() => {
    if (rangeMode !== 'lookback') return;
    try {
      const raw = Number(lookbackValue);
      const v = Number.isFinite(raw) ? raw : 7;
      const unit = lookbackUnit === 'hours' ? 'hours' : 'days';
      const ms = unit === 'hours' ? v * 3600 * 1000 : v * 24 * 3600 * 1000;
      const end = new Date();
      const start = new Date(Date.now() - ms);
      setEndDate(toDateInput(end));
      setStartDate(toDateInput(start));
    } catch {
      // ignore
    }
  }, [rangeMode, lookbackUnit, lookbackValue]);

  const statusOptions = useMemo(
    () => [
      { k: 'ALL', label: 'ALL' },
      { k: 'processing', label: 'Processing' },
      { k: 'shipped', label: 'Shipped' },
      { k: 'cancelled', label: 'Cancelled' },
      { k: 'refunded', label: 'Refunded' },
      { k: 'open', label: 'Open (POS)' },
      { k: 'ready_for_pickup', label: 'Ready for pickup (POS)' },
      { k: 'pickup_complete', label: 'Pickup complete (POS)' },
      { k: 'pending_payment', label: 'Pending payment (POS)' },
      { k: 'paid', label: 'Paid (POS)' },
    ],
    []
  );

  const productById = useMemo(() => {
    const out = {};
    safeArray(products).forEach((p) => {
      const pid = String(p?.ProductID || p?.productId || '').trim();
      if (!pid) return;
      out[pid] = p;
    });
    return out;
  }, [products]);

  const availableCategories = useMemo(() => {
    const uniq = new Set();
    for (const p of safeArray(products)) {
      const cat = String(p?.Category || p?.category || '').trim();
      if (cat) uniq.add(cat);
    }
    return ['ALL', ...Array.from(uniq).sort((a, b) => a.localeCompare(b))];
  }, [products]);

  const availableMeals = useMemo(() => {
    const uniq = new Set();
    for (const p of safeArray(products)) {
      const raw = p?.AvailableDuring ?? p?.availableDuring ?? p?.available_during ?? p?.availableDuringWindows;
      safeArray(raw)
        .map((x) => String(x || '').trim())
        .filter(Boolean)
        .forEach((v) => uniq.add(v));
    }
    return ['ALL', ...Array.from(uniq).sort((a, b) => a.localeCompare(b))];
  }, [products]);

  const channels = useMemo(() => {
    const uniq = new Set();
    safeArray(orders).forEach((o) => {
      const c = normalizeChannel(o);
      if (c) uniq.add(c);
    });
    return ['ALL', ...Array.from(uniq).sort((a, b) => a.localeCompare(b))];
  }, [orders]);

  const resolvedGranularity = useMemo(() => {
    if (granularity === 'hour' || granularity === 'day') return granularity;
    try {
      const start = new Date(`${startDate}T00:00:00`);
      const end = new Date(`${endDate}T00:00:00`);
      const diff = end.getTime() - start.getTime();
      const days = diff / (24 * 3600 * 1000);
      return days <= 2 ? 'hour' : 'day';
    } catch {
      return 'day';
    }
  }, [granularity, startDate, endDate]);

  const fetchProducts = React.useCallback(async (token) => {
    if (!API_BASE_URL) return;
    const res = await fetch(`${API_BASE_URL}/admin/products`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Failed to fetch products');
    setProducts(Array.isArray(data?.products) ? data.products : []);
  }, []);

  const runReport = React.useCallback(async () => {
    setError('');
    setStatusMessage('');

    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please set start and end date.');
      return;
    }

    try {
      setSearching(true);
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      await fetchProducts(token);

      const url = new URL(`${API_BASE_URL}/admin/orders/report`);
      url.searchParams.set('start', startDate);
      url.searchParams.set('end', endDate);
      url.searchParams.set('include_items', '1');
      if (orderStatusFilter && orderStatusFilter !== 'ALL') {
        url.searchParams.set('status', orderStatusFilter);
      }
      if (locationFilter && locationFilter !== 'ALL') {
        url.searchParams.set('location_id', locationFilter);
      }

      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to run report');

      const list = Array.isArray(data?.orders) ? data.orders : [];
      setOrders(list);
      setStatusMessage(`Loaded ${list.length} order(s).`);
    } catch (e) {
      setOrders([]);
      setError(e?.message || 'Failed to run report');
    } finally {
      setSearching(false);
    }
  }, [startDate, endDate, orderStatusFilter, fetchProducts]);

  useEffect(() => {
    if (loading) return;
    runReport();
  }, [loading, runReport]);

  const filteredOrders = useMemo(() => {
    let out = safeArray(orders);

    if (channelFilter && channelFilter !== 'ALL') {
      out = out.filter((o) => normalizeChannel(o) === channelFilter);
    }

    if (payTypeFilter && payTypeFilter !== 'ALL') {
      out = out.filter((o) => {
        const pt = normalizePayType(o);
        return payTypeFilter === 'PAY_AT_STORE' ? pt === 'pay_at_store' : pt === 'pay_online';
      });
    }

    if ((categoryFilter && categoryFilter !== 'ALL') || (mealFilter && mealFilter !== 'ALL')) {
      out = out.filter((o) => {
        const items = safeArray(o?.items);
        if (!items.length) return false;

        return items.some((it) => {
          const pid = String(it?.ProductID || it?.productId || '').trim();
          const product = pid ? productById[pid] : null;

          if (categoryFilter && categoryFilter !== 'ALL') {
            const cat = String(product?.Category || product?.category || '').trim();
            if (!cat || cat !== categoryFilter) return false;
          }

          if (mealFilter && mealFilter !== 'ALL') {
            const raw = product?.AvailableDuring ?? product?.availableDuring ?? product?.available_during ?? product?.availableDuringWindows;
            const list = safeArray(raw).map((x) => String(x || '').trim()).filter(Boolean);
            if (!list.some((v) => v.toLowerCase() === mealFilter.toLowerCase())) return false;
          }

          return true;
        });
      });
    }

    return out;
  }, [orders, channelFilter, payTypeFilter, categoryFilter, mealFilter, productById]);

  const totalSalesCents = useMemo(() => sumCents(filteredOrders), [filteredOrders]);
  const totalOrders = useMemo(() => filteredOrders.length, [filteredOrders]);
  const avgOrderValueCents = useMemo(() => (totalOrders ? Math.round(totalSalesCents / totalOrders) : 0), [totalOrders, totalSalesCents]);

  const paySplit = useMemo(() => {
    const out = {
      pay_online: { orders: 0, sales_cents: 0 },
      pay_at_store: { orders: 0, sales_cents: 0 },
    };
    for (const o of filteredOrders) {
      const pt = normalizePayType(o);
      const cents = o?.amount_total_cents ?? o?.amount_total;
      const n = Number(cents);
      const add = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
      out[pt].orders += 1;
      out[pt].sales_cents += add;
    }
    return out;
  }, [filteredOrders]);

  const fulfillmentSplit = useMemo(() => {
    const out = {
      delivery: { orders: 0, sales_cents: 0 },
      pickup: { orders: 0, sales_cents: 0 },
    };
    for (const o of filteredOrders) {
      // Check fulfillment_type, or fallback to checking if delivery_address exists
      let ft = String(o?.fulfillment_type || '').trim().toLowerCase();
      if (!ft && o?.delivery_address) {
        ft = 'delivery';
      }
      if (!ft) {
        ft = 'pickup';
      }
      const key = ft === 'delivery' ? 'delivery' : 'pickup';
      const cents = o?.amount_total_cents ?? o?.amount_total;
      const n = Number(cents);
      const add = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
      out[key].orders += 1;
      out[key].sales_cents += add;
    }
    return out;
  }, [filteredOrders]);

  const unpaidOrders = useMemo(() => {
    return filteredOrders.filter((o) => {
      if (normalizePayType(o) !== 'pay_at_store') return false;
      const pay = String(o?.displayPaymentStatus || o?.payment_status || o?.paymentStatus || '').trim().toUpperCase();
      return pay === 'PENDING_PAYMENT' || !pay;
    }).length;
  }, [filteredOrders]);

  const paymentDonutData = useMemo(() => {
    return [
      { name: 'Pay Online', value: paySplit.pay_online.sales_cents, key: 'pay_online' },
      { name: 'Pay at Store', value: paySplit.pay_at_store.sales_cents, key: 'pay_at_store' },
    ];
  }, [paySplit]);

  const fulfillmentDonutData = useMemo(() => {
    return [
      { name: 'Delivery', value: fulfillmentSplit.delivery.sales_cents, key: 'delivery' },
      { name: 'Pickup', value: fulfillmentSplit.pickup.sales_cents, key: 'pickup' },
    ];
  }, [fulfillmentSplit]);

  const trendSeries = useMemo(() => {
    const map = new Map();
    for (const o of filteredOrders) {
      const ts = o?.paid_at || o?.order_timestamp;
      const key = bucketKey(ts, resolvedGranularity);
      if (!key) continue;

      const existing = map.get(key) || {
        bucket: key,
        payOnlineRevenueCents: 0,
        payAtStoreRevenueCents: 0,
        deliveryRevenueCents: 0,
        pickupRevenueCents: 0,
        orders: 0,
      };

      const cents = o?.amount_total_cents ?? o?.amount_total;
      const n = Number(cents);
      const add = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
      
      // Payment type split
      if (normalizePayType(o) === 'pay_at_store') existing.payAtStoreRevenueCents += add;
      else existing.payOnlineRevenueCents += add;
      
      // Fulfillment type split - check fulfillment_type or fallback to delivery_address
      let ft = String(o?.fulfillment_type || '').trim().toLowerCase();
      if (!ft && o?.delivery_address) {
        ft = 'delivery';
      }
      if (!ft) {
        ft = 'pickup';
      }
      if (ft === 'delivery') existing.deliveryRevenueCents += add;
      else existing.pickupRevenueCents += add;
      
      existing.orders += 1;

      map.set(key, existing);
    }

    const list = Array.from(map.values());
    list.sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
    return list.map((r) => ({
      bucket: r.bucket,
      payOnlineRevenue: r.payOnlineRevenueCents / 100,
      payAtStoreRevenue: r.payAtStoreRevenueCents / 100,
      deliveryRevenue: r.deliveryRevenueCents / 100,
      pickupRevenue: r.pickupRevenueCents / 100,
      orders: r.orders,
      revenue: (r.payOnlineRevenueCents + r.payAtStoreRevenueCents) / 100,
    }));
  }, [filteredOrders, resolvedGranularity]);

  const comboSeries = useMemo(() => {
    return trendSeries.map((r) => ({ bucket: r.bucket, orders: r.orders, revenue: r.revenue }));
  }, [trendSeries]);

  const topItems = useMemo(() => {
    const map = new Map();

    for (const o of filteredOrders) {
      for (const it of safeArray(o?.items)) {
        const pid = String(it?.ProductID || it?.productId || '').trim();
        const key = pid || String(it?.name || it?.ProductName || '').trim();
        if (!key) continue;

        const qtyRaw = it?.quantity ?? it?.Quantity;
        const qty = Number.isFinite(Number(qtyRaw)) ? Math.max(0, Math.floor(Number(qtyRaw))) : 0;

        const prod = pid ? productById[pid] : null;

        const baseFromItemCents = Number(
          it?.price_cents ?? it?.priceCents ?? it?.unitPriceCents ?? it?.unit_price_cents ?? 0
        );
        const baseFromItemDollars = parseDollarsToCents(it?.price ?? it?.Price ?? it?.unitPrice ?? it?.UnitPrice);
        const baseFromCatalogDollars = parseDollarsToCents(prod?.Price ?? prod?.price);

        let base = Number.isFinite(baseFromItemCents) ? baseFromItemCents : 0;
        if (!base || base <= 0) base = baseFromItemDollars;
        if (!base || base <= 0) base = baseFromCatalogDollars;

        const mod = Number(it?.modifier_price_cents ?? it?.modifierPriceCents ?? 0);
        const unit = (Number.isFinite(base) ? base : 0) + (Number.isFinite(mod) ? mod : 0);
        const revenue = qty * Math.max(0, Math.floor(unit));

        const existing = map.get(key) || {
          key,
          productId: pid,
          name: '',
          quantity: 0,
          revenueCents: 0,
          category: '',
          meals: '',
        };

        existing.quantity += qty;
        existing.revenueCents += revenue;

        const displayName = String(prod?.ProductName || prod?.name || it?.name || key).trim();
        if (displayName) existing.name = displayName;
        if (prod) {
          const cat = String(prod?.Category || prod?.category || '').trim();
          if (cat) existing.category = cat;
          const rawMeals = prod?.AvailableDuring ?? prod?.availableDuring ?? prod?.available_during ?? prod?.availableDuringWindows;
          const meals = safeArray(rawMeals).map((x) => String(x || '').trim()).filter(Boolean);
          if (meals.length) existing.meals = meals.join(' | ');
        }

        map.set(key, existing);
      }
    }

    const list = Array.from(map.values());
    list.sort((a, b) => Number(b.revenueCents) - Number(a.revenueCents));
    return list.slice(0, 12);
  }, [filteredOrders, productById]);

  const topItemsChartData = useMemo(() => {
    return topItems.map((it, idx) => {
      const fullName = it.name || it.key;
      const safeName = String(fullName || '').trim() || `Item ${idx + 1}`;
      const short = safeName.length > 40 ? `${safeName.slice(0, 37)}...` : safeName;
      return {
        rank: idx + 1,
        label: `${idx + 1}. ${short}`,
        fullName: safeName,
        revenue: Number((it.revenueCents || 0) / 100),
        quantity: Number(it.quantity || 0),
      };
    });
  }, [topItems]);

  const topItemsChartHeight = useMemo(() => {
    const rows = topItemsChartData.length || 0;
    return Math.max(340, rows * 34);
  }, [topItemsChartData.length]);

  const topItemsChartDataForDisplay = useMemo(() => {
    const list = safeArray(topItemsChartData);
    if (topMode === 'quantity') {
      return list.filter((r) => Number(r?.quantity || 0) > 0);
    }
    return list.filter((r) => Number(r?.revenue || 0) > 0);
  }, [topItemsChartData, topMode]);

  const topItemsChartHeightForDisplay = useMemo(() => {
    const rows = topItemsChartDataForDisplay.length || 0;
    return Math.max(340, rows * 34);
  }, [topItemsChartDataForDisplay.length]);

  const exportRawOrders = () => {
    const rows = filteredOrders.map((o) => {
      const cents = o?.amount_total_cents ?? o?.amount_total;
      const n = Number(cents);
      const revenue = Number.isFinite(n) ? Math.floor(n) : 0;
      return {
        order_number: o?.order_number || '',
        order_timestamp: o?.order_timestamp || '',
        paid_at: o?.paid_at || '',
        channel: normalizeChannel(o),
        pay_type: normalizePayType(o) === 'pay_at_store' ? 'PAY_AT_STORE' : 'PAY_ONLINE',
        payment_status: String(o?.displayPaymentStatus || o?.payment_status || o?.paymentStatus || '').trim(),
        status: String(o?.displayStatus || o?.status || '').trim(),
        total_cents: revenue,
        total: formatMoneyFromCents(revenue),
        items_count: safeArray(o?.items).length,
      };
    });

    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv({ filename: `online-sales-raw-orders-${stamp}.csv`, rows });
  };

  const exportTopItems = () => {
    const rows = topItems.map((it) => ({
      product_id: it.productId || '',
      name: it.name || it.key,
      quantity: it.quantity,
      revenue_cents: it.revenueCents,
      revenue: formatMoneyFromCents(it.revenueCents),
      category: it.category || '',
      meals: it.meals || '',
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv({ filename: `online-sales-top-items-${stamp}.csv`, rows });
  };

  const exportSeries = () => {
    const rows = trendSeries.map((r) => ({
      bucket: r.bucket,
      pay_online_revenue: r.payOnlineRevenue,
      pay_at_store_revenue: r.payAtStoreRevenue,
      orders: r.orders,
      total_revenue: r.revenue,
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv({ filename: `online-sales-series-${stamp}.csv`, rows });
  };

  const COLORS = {
    pay_online: '#22c55e',
    pay_at_store: '#fb923c',
    delivery: '#3b82f6',
    pickup: '#8b5cf6',
  };

  const TOP_ITEM_COLORS = [
    '#7c3aed',
    '#2563eb',
    '#0891b2',
    '#16a34a',
    '#ea580c',
    '#db2777',
    '#4f46e5',
    '#0f766e',
    '#65a30d',
    '#c2410c',
    '#be185d',
    '#6d28d9',
  ];

  const payOnlineLabel = useMemo(() => formatMoneyFromCents(paySplit.pay_online.sales_cents), [paySplit]);
  const payAtStoreLabel = useMemo(() => formatMoneyFromCents(paySplit.pay_at_store.sales_cents), [paySplit]);
  const deliveryLabel = useMemo(() => formatMoneyFromCents(fulfillmentSplit.delivery.sales_cents), [fulfillmentSplit]);
  const pickupLabel = useMemo(() => formatMoneyFromCents(fulfillmentSplit.pickup.sales_cents), [fulfillmentSplit]);

  const filterRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
    alignItems: 'end',
  };

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Orders/Reports');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        
        // Load locations
        try {
          if (API_BASE_URL) {
            const token = session.getIdToken().getJwtToken();
            const res = await fetch(`${API_BASE_URL}/admin/locations`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              const list = Array.isArray(data?.locations) ? data.locations : [];
              setLocations(list);
            }
          }
        } catch (e) {
          console.error('Failed to load locations:', e);
        }
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-chart-line"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div className="admin-header-welcome">
            <h1>Online Sales Report</h1>
            <p>Revenue + payment insights across channels</p>
          </div>
        </div>

        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <nav className="reports-subtabs" aria-label="Reports navigation">
        <NavLink to="/admin/reports/orders" className={({ isActive }) => `reports-subtab${isActive ? ' active' : ''}`}>
          Orders
        </NavLink>
        <NavLink to="/admin/reports/online-sales" className={({ isActive }) => `reports-subtab${isActive ? ' active' : ''}`}>
          Online Sales
        </NavLink>
        <NavLink to="/admin/reports/fulfillment" className={({ isActive }) => `reports-subtab${isActive ? ' active' : ''}`}>
          Fulfillment
        </NavLink>
      </nav>

      <div className="admin-content">
        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        <div className="product-form-container">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h2>Report Filters</h2>
              <p style={{ marginTop: 6, color: 'rgba(15, 23, 42, 0.65)' }}>
                Sales across all channels, with Pay Online vs Pay at Store split.
              </p>
            </div>

            <div className="report-actions">
              <button className="admin-action-btn" onClick={runReport} disabled={searching}>
                {searching ? 'Loading…' : 'Run report'}
              </button>
              <button className="admin-action-btn" onClick={exportRawOrders} disabled={filteredOrders.length === 0}>
                Export orders CSV
              </button>
              <button className="admin-action-btn" onClick={exportTopItems} disabled={topItems.length === 0}>
                Export top items CSV
              </button>
              <button className="admin-action-btn" onClick={exportSeries} disabled={trendSeries.length === 0}>
                Export series CSV
              </button>
            </div>
          </div>

          <div className="form-row" style={filterRowStyle}>
            <div className="form-group">
              <label>Range mode</label>
              <select value={rangeMode} onChange={(e) => setRangeMode(e.target.value === 'custom' ? 'custom' : 'lookback')}>
                <option value="lookback">Lookback</option>
                <option value="custom">Custom dates</option>
              </select>
            </div>

            {rangeMode === 'lookback' ? (
              <>
                <div className="form-group">
                  <label>Lookback</label>
                  <input
                    type="number"
                    min="1"
                    max={lookbackUnit === 'hours' ? '720' : '365'}
                    value={lookbackValue}
                    onChange={(e) => setLookbackValue(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <select value={lookbackUnit} onChange={(e) => setLookbackUnit(e.target.value === 'hours' ? 'hours' : 'days')}>
                    <option value="days">Days</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label>Start</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>End</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </>
            )}
          </div>

          <div className="form-row" style={filterRowStyle}>
            <div className="form-group">
              <label>Status</label>
              <select value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}>
                {statusOptions.map((opt) => (
                  <option key={`status-${opt.k}`} value={opt.k}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Location</label>
              <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                <option value="ALL">ALL LOCATIONS</option>
                {locations.map((loc) => (
                  <option key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Channel</label>
              <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}>
                {channels.map((c) => (
                  <option key={`channel-${c}`} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Payment type</label>
              <select value={payTypeFilter} onChange={(e) => setPayTypeFilter(e.target.value)}>
                <option value="ALL">ALL</option>
                <option value="PAY_ONLINE">Pay online</option>
                <option value="PAY_AT_STORE">Pay at store</option>
              </select>
            </div>

            <div className="form-group">
              <label>Meal</label>
              <select value={mealFilter} onChange={(e) => setMealFilter(e.target.value)}>
                {availableMeals.map((m) => (
                  <option key={`meal-${m}`} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                {availableCategories.map((c) => (
                  <option key={`cat-${c}`} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Trend bucket</label>
              <select value={granularity} onChange={(e) => setGranularity(e.target.value)}>
                <option value="auto">Auto</option>
                <option value="day">Day</option>
                <option value="hour">Hour</option>
              </select>
            </div>
          </div>
        </div>

        <section className="fulfillment-kpi-grid">
          <div className="fulfillment-kpi-card">
            <div className="fulfillment-kpi-title">Total sales</div>
            <div className="fulfillment-kpi-value">{formatMoneyFromCents(totalSalesCents)}</div>
            <div className="fulfillment-kpi-sub">{totalOrders.toLocaleString()} order(s)</div>
          </div>
          <div className="fulfillment-kpi-card">
            <div className="fulfillment-kpi-title">Total orders</div>
            <div className="fulfillment-kpi-value">{totalOrders.toLocaleString()}</div>
            <div className="fulfillment-kpi-sub">Filtered view</div>
          </div>
          <div className="fulfillment-kpi-card">
            <div className="fulfillment-kpi-title">Avg order value</div>
            <div className="fulfillment-kpi-value">{formatMoneyFromCents(avgOrderValueCents)}</div>
            <div className="fulfillment-kpi-sub">Sales / Orders</div>
          </div>
          <div className="fulfillment-kpi-card">
            <div className="fulfillment-kpi-title">Pending pay-at-store</div>
            <div className="fulfillment-kpi-value">{Number(unpaidOrders || 0).toLocaleString()}</div>
            <div className="fulfillment-kpi-sub">Pay at store + pending payment</div>
          </div>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 18 }}>
          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Payment split</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pay_online}1a`,
                    border: `1px solid ${COLORS.pay_online}66`,
                    color: '#0f172a',
                  }}
                >
                  {payOnlineLabel} online
                </span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pay_at_store}1a`,
                    border: `1px solid ${COLORS.pay_at_store}66`,
                    color: '#0f172a',
                  }}
                >
                  {payAtStoreLabel} at store
                </span>
              </div>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Tooltip
                      formatter={(v) => formatMoneyFromCents(v)}
                      contentStyle={{ borderRadius: 12 }}
                    />
                    <Legend />
                    <Pie data={paymentDonutData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={92} paddingAngle={3}>
                      {paymentDonutData.map((entry) => (
                        <Cell key={entry.key} fill={COLORS[entry.key] || '#94a3b8'} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Fulfillment split</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.delivery}1a`,
                    border: `1px solid ${COLORS.delivery}66`,
                    color: '#0f172a',
                  }}
                >
                  {deliveryLabel} delivery
                </span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pickup}1a`,
                    border: `1px solid ${COLORS.pickup}66`,
                    color: '#0f172a',
                  }}
                >
                  {pickupLabel} pickup
                </span>
              </div>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Tooltip
                      formatter={(v) => formatMoneyFromCents(v)}
                      contentStyle={{ borderRadius: 12 }}
                    />
                    <Legend />
                    <Pie data={fulfillmentDonutData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={92} paddingAngle={3}>
                      {fulfillmentDonutData.map((entry) => (
                        <Cell key={entry.key} fill={COLORS[entry.key] || '#94a3b8'} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Fulfillment trend</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="status-pill">Bucket: {resolvedGranularity}</span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.delivery}1a`,
                    border: `1px solid ${COLORS.delivery}66`,
                    color: '#0f172a',
                  }}
                >
                  {deliveryLabel}
                </span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pickup}1a`,
                    border: `1px solid ${COLORS.pickup}66`,
                    color: '#0f172a',
                  }}
                >
                  {pickupLabel}
                </span>
              </div>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={trendSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}`} />
                    <Tooltip formatter={(v) => `${Number(v).toFixed(2)}`} />
                    <Legend />
                    <Line type="monotone" dataKey="deliveryRevenue" name="Delivery" stroke={COLORS.delivery} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="pickupRevenue" name="Pickup" stroke={COLORS.pickup} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Sales trend</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="status-pill">Bucket: {resolvedGranularity}</span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pay_online}1a`,
                    border: `1px solid ${COLORS.pay_online}66`,
                    color: '#0f172a',
                  }}
                >
                  {payOnlineLabel}
                </span>
                <span
                  className="status-pill"
                  style={{
                    background: `${COLORS.pay_at_store}1a`,
                    border: `1px solid ${COLORS.pay_at_store}66`,
                    color: '#0f172a',
                  }}
                >
                  {payAtStoreLabel}
                </span>
              </div>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={trendSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                    <Legend />
                    <Line type="monotone" dataKey="payOnlineRevenue" name="Pay Online" stroke={COLORS.pay_online} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="payAtStoreRevenue" name="Pay at Store" stroke={COLORS.pay_at_store} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Orders vs revenue</h3>
              <span className="status-pill">{trendSeries.length} bucket(s)</span>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={comboSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v, name) => (name === 'Revenue' ? `$${Number(v).toFixed(2)}` : v)} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="orders" name="Orders" fill="#60a5fa" radius={[10, 10, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#7c3aed" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Top selling items</h3>
              <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="admin-mini-btn"
                  onClick={() => setTopMode('revenue')}
                  style={{ opacity: topMode === 'revenue' ? 1 : 0.7 }}
                >
                  By revenue
                </button>
                <button
                  className="admin-mini-btn"
                  onClick={() => setTopMode('quantity')}
                  style={{ opacity: topMode === 'quantity' ? 1 : 0.7 }}
                >
                  By qty
                </button>
              </div>
            </div>

            <div className="fulfillment-chart-body">
              <div style={{ width: '100%', height: topItemsChartHeightForDisplay }}>
                <ResponsiveContainer>
                  <BarChart data={topItemsChartDataForDisplay} layout="vertical" margin={{ left: 20, right: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="label" width={170} tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12 }}
                      formatter={(v, name, p) => {
                        const n = Number(v);
                        const label = topMode === 'quantity' ? 'Qty' : 'Revenue';
                        const val = topMode === 'quantity' ? (Number.isFinite(n) ? n : 0) : `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
                        return [val, label];
                      }}
                      labelFormatter={(label, payload) => {
                        const p = Array.isArray(payload) && payload.length ? payload[0]?.payload : null;
                        return p?.fullName || label;
                      }}
                    />
                    <Bar
                      dataKey={topMode === 'quantity' ? 'quantity' : 'revenue'}
                      name={topMode === 'quantity' ? 'Quantity' : 'Revenue'}
                      radius={[0, 10, 10, 0]}
                      isAnimationActive={false}
                    >
                      {topItemsChartDataForDisplay.map((entry, idx) => (
                        <Cell key={`cell-${entry.rank || idx}`} fill={TOP_ITEM_COLORS[idx % TOP_ITEM_COLORS.length]} />
                      ))}
                      <LabelList
                        dataKey={topMode === 'quantity' ? 'quantity' : 'revenue'}
                        position="right"
                        formatter={(v) => {
                          const n = Number(v);
                          if (!Number.isFinite(n)) return '';
                          return topMode === 'quantity' ? `${Math.round(n)}` : `$${n.toFixed(2)}`;
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="fulfillment-chart-card">
            <div className="fulfillment-chart-header">
              <h3>Insights</h3>
              <span className="status-pill info">Auto</span>
            </div>
            <div className="fulfillment-chart-body">
              <div style={{ display: 'grid', gap: 10 }}>
                <div className="status-pill">
                  Pay online: {formatMoneyFromCents(paySplit.pay_online.sales_cents)}
                </div>
                <div className="status-pill warning">
                  Pay at store: {formatMoneyFromCents(paySplit.pay_at_store.sales_cents)}
                </div>
                <div className="status-pill">
                  Pay at store share:{' '}
                  {totalSalesCents > 0
                    ? `${Math.round((paySplit.pay_at_store.sales_cents / totalSalesCents) * 100)}%`
                    : '0%'}
                </div>
                <div style={{ color: 'rgba(15, 23, 42, 0.7)', fontSize: '0.92rem', lineHeight: 1.4 }}>
                  Use meal/category filters to isolate what drives pay-at-store volume. Pending pay-at-store is a quick proxy for
                  unpaid/no-show risk.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
